import { Component, ElementRef, forwardRef, OnInit, Input, ViewChild, ChangeDetectorRef, NgZone, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs/Subscription';
import { PayorProfitCentersService } from 'app/pages/payors/payor-profit-centers/payor-profit-centers.service';
import { EmailsExternalCompanies } from 'app/core/enums/emails-external-companies.enum';
import { SweetAlertHelper } from 'app/core/helpers/sweet-alert-helper.service';

@Component({
  selector: 'app-input-emails',
  templateUrl: './input-emails.component.html',
  styleUrls: ['./input-emails.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputEmailsComponent),
      multi: true
    }
  ]
})
export class InputEmailsComponent implements OnInit, ControlValueAccessor, OnDestroy {
  subscriptions: Subscription[] = [];
  @Input() autocomplete = false;
  @Input() suggestedEmails: string[] = [];
  @ViewChild('dropdown') dropdown: ElementRef;
  public pattern: RegExp;
  public emails: string[];
  public email: string;
  public placeholder: string;
  public isDisabled = false;
  public selectedIndex: number = -1;
  filteredEmails: string[] = [];
  openUpwards = false;

  constructor(
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone,
    private sweetAlertHelper: SweetAlertHelper,
    private payorProfitCentersService: PayorProfitCentersService,
  ) {
    this.pattern = /^([a-z0-9]+(?:[._-][a-z0-9]+)*)@([a-z0-9]+(?:[.-][a-z0-9]+)*\.[a-z]{2,})$/i;

    this.emails = [];
    this.email = '';
    this.placeholder = 'Type emails';
  }

  ngOnInit() {
    this.subscriptions.push(
      this.payorProfitCentersService.validateEmailsDomains$.subscribe((value) => {
        if (value) {
          if (this.suggestedEmails) {
            const filtered = this.suggestedEmails.filter(email =>
              email && EmailsExternalCompanies.validDomains.some(domain => email.toLowerCase().endsWith(domain))
            );

            this.suggestedEmails = filtered;
          }
        }
      }),
    );
  }

  onAddEmail(event: any) {
    if (this.isDisabled) {
      return;
    };
    if (event.keyCode !== 16) {
      if (
        event.keyCode === 13 ||
        event.keyCode === 32 ||
        event.keyCode === 188
      ) {
        event.preventDefault();
        if (this.email !== '') {
          if (this.pattern.test(this.email)) {
            this.emails.push(this.email);
            this.propagateChange(this.emails.join(';'));
            this.email = '';
          } else {
            this.sweetAlertHelper.captureException(`Please enter a valid email address (e.g., your_name@example.com).`);
          }
        }
      }
    }
  }

  onTyping(event: any) {
    if (!this.autocomplete) {
      return;
    }
    const valor = event.target.value;
    if (!valor) {
      this.filteredEmails = [];
      return;
    }
    this.filteredEmails = this.suggestedEmails.filter(
      (email) => email && email.toLowerCase().includes(valor.toLowerCase())
    );

    this.checkDropdownPosition(event.target as HTMLElement);
  }

  checkDropdownPosition(inputEl: HTMLElement) {
    const rect = inputEl.getBoundingClientRect();
    const dropdownHeight = 330;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    this.openUpwards =
      spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  }


  selectEmail(email: string) {
    if (!this.autocomplete) {
      return;
    }
    this.addEmail(email);
    this.filteredEmails = [];
  }

  addEmail(email: string) {
    if (!email) {
      return;
    }
    if (!this.emails.includes(email)) {
      this.emails.push(email);
      this.propagateChange(this.emails.join(';'));
      this.email = '';
    }
  }

  onKeyDown(event: KeyboardEvent) {
    if (!this.autocomplete || this.filteredEmails.length === 0){
      return;
    }

    switch (event.key) {
      case "ArrowDown":
        this.moveSelection(1);
        event.preventDefault();
        break;
      case "ArrowUp":
        this.moveSelection(-1);
        event.preventDefault();
        break;
      case "Enter":
        this.selectActiveEmail(event);
        break;
    }
  }

  private moveSelection(direction: number) {
    const length = this.filteredEmails.length;
    this.selectedIndex = (this.selectedIndex + direction + length) % length;
    this.scrollToActive();
  }

  private selectActiveEmail(event: KeyboardEvent) {
    if (this.selectedIndex >= 0) {
      this.selectEmail(this.filteredEmails[this.selectedIndex]);
      this.selectedIndex = -1;
      event.preventDefault();
    }
  }

  scrollToActive() {
    if (!this.dropdown) {
      return;
    }

    this.cdRef.detectChanges();
    const sub = this.ngZone.onStable.pipe().subscribe(() => {
      const items = this.dropdown.nativeElement.querySelectorAll("li");
      if (items.length === 0 || this.selectedIndex < 0){
        return;
      }
      const activeItem = items[this.selectedIndex];
      activeItem.scrollIntoView({ block: "nearest" });
    });
    this.subscriptions.push(sub)
  }

  onKeyDownCombined(event: KeyboardEvent) {
    this.onAddEmail(event);
    this.onKeyDown(event);
  }

  onMouseEnter(index: number) {
    this.selectedIndex = index;
  }

  onMouseClick(email: string, index: number) {
    this.selectedIndex = index;
    this.selectEmail(email);
  }

  onMouseLeaveDropdown() {
    this.selectedIndex = -1;
  }

  onDelete(email: string): void {
    if (this.isDisabled){
      return;
    };
    this.emails.forEach((node, index) => {
      if (node === email) {
        this.emails.splice(index, 1);
        this.propagateChange(this.emails.join(';'));
      }
    });
  }

  writeValue(obj: any): void {
    this.emails = [];
    this.email = '';
    this.filteredEmails = [];
    this.selectedIndex = -1;
    if (obj !== null && obj !== undefined && obj !== '' && obj !== ' ') {
      if (Array.isArray(obj)) {
        this.emails = obj;
      } else {
        this.emails = obj.split(';');
      }
    }
  }

  propagateChange = (_: any) => {};

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  onFocusout() {
    if (this.isDisabled) {
      return;
    }
    if (this.email !== '') {
      if (this.pattern.test(this.email)) {
        this.emails.push(this.email);
        this.propagateChange(this.emails.join(';'));
        this.email = '';
      } else {
        this.sweetAlertHelper.captureException(`Please enter a valid email address (e.g., your_name@example.com).`);
        this.email = '';
      }
    }
    this.selectedIndex = -1;
    this.filteredEmails = [];
  }

  disabledAutocomplete() {
    const randomValue = 'off' + Math.random().toString();
    const inputElement = document.getElementById('emailIn') as HTMLInputElement;
    inputElement.setAttribute('autocomplete', randomValue);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/utils/emails-input/components/input-emails/input-emails.component.ts